<template>
  <BaseModal
    :model-value="isOpen"
    :title="$t('commerce.moreFiltersModal.moreFilters')"
    max-width-class="max-w-2xl"
    panel-class="2xl:h-auto h-[60vh]"
    @close="closeModal"
  >
    <div>
      <div>
        <!-- Other Filters Section -->
        <div class="mb-8">
          <div class="space-y-4">
            <!--
              <div class="relative flex items-start">
              <div class="flex h-6 items-center">
              <SharedAppCheckbox
              id="individual-packaging"
              v-model="filters.individualPackaging"
              name="individual-packaging"
              />
              </div>
              <div class="ml-3 text-sm leading-6">
              <label
              for="individual-packaging"
              class="font-medium text-content-level-900 dark:text-content-inverse"
              >Individual Packaging</label>
              <p class="text-content-level-500 dark:text-content-level-400">
              Restaurants that offer individually packaged items
              </p>
              </div>
              </div>
            -->

            <div class="flex items-start relative">
              <div class="flex h-6 items-center">
                <SharedAppCheckbox
                  id="is-new"
                  :model-value="filters.isNew"
                  name="is-new"
                  @update:model-value="setIsNew"
                />
              </div>

              <div class="leading-6 ml-3 text-sm">
                <label
                  for="is-new"
                  class="dark:text-content-inverse font-medium text-content-level-900"
                >
                  {{ $t('commerce.moreFiltersModal.new') }}
                </label>

                <p class="dark:text-content-level-400 text-content-level-500">
                  {{ $t('commerce.moreFiltersModal.restaurantsThatAreNewToMealz') }}
                </p>
              </div>
            </div>

            <div class="flex items-start relative">
              <div class="flex h-6 items-center">
                <SharedAppCheckbox
                  id="small-business"
                  :model-value="filters.smallBusiness"
                  name="small-business"
                  @update:model-value="setSmallBusiness"
                />
              </div>

              <div class="leading-6 ml-3 text-sm">
                <label
                  for="small-business"
                  class="dark:text-content-inverse font-medium text-content-level-900"
                >
                  {{ $t('commerce.moreFiltersModal.smallBusiness') }}
                </label>

                <p class="dark:text-content-level-400 text-content-level-500">
                  <I18nT
                    scope="global"
                    keypath="commerce.moreFiltersModal.seeHowWeIdentifyAndHighlightSmallBusinesses"
                    ><template #supplierDiversityFaq
                      ><a
                        href="#"
                        class="dark:hover:text-primary-light hover:text-primary-dark text-primary underline"
                      >
                        {{ $t('commerce.moreFiltersModal.supplierDiversityFaq') }}
                      </a></template
                    ></I18nT
                  >
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Distance filter Section -->
        <div class="mb-8">
          <div class="gap-6 grid grid-cols-1 sm:grid-cols-2">
            <div>
              <label
                for="distance"
                class="block dark:text-content-inverse font-medium text-content-level-900 text-sm/6"
              >
                {{ $t('commerce.moreFiltersModal.distance') }}
              </label>

              <div class="mt-2">
                <Select
                  id="distance"
                  v-model="distanceSelection"
                  :options="distanceOptions"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <button
        type="button"
        class="bg-surface-canvas border border-border-level-300 btn btn-active dark:bg-surface-clear dark:border-border-level-600"
        @click="clear"
      >
        {{ $t('commerce.moreFiltersModal.clearFilter') }}
      </button>

      <button
        type="button"
        class="btn btn-active btn-primary text-content-inverse"
        @click="apply"
      >
        {{ $t('commerce.moreFiltersModal.applyFilter') }}
      </button>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useMoreFiltersDraft } from '~/composables/useMoreFiltersDraft'
import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import Select from '~/design-system/atoms/Select/Select.atom.vue'
import type { SelectOption } from '~/design-system/atoms/Select/Select.atom.vue'

const props = withDefaults(
  defineProps<{
    /** Controls whether the modal is visible. */
    isOpen?: boolean
  }>(),
  { isOpen: false }
)

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()

// A select item cannot carry an empty value, so "any" travels as this key.
const ANY_OPTION = 'any'

type AnyAwareField = 'distance'

const anyAwareSelection = (field: AnyAwareField) =>
  computed<string>({
    get: () => (filters[field] === '' ? ANY_OPTION : filters[field]),
    set: (value) => {
      filters[field] = value === ANY_OPTION ? '' : value
    }
  })

const distanceOptions = computed<SelectOption[]>(() => [
  { label: t('commerce.moreFiltersModal.anyDistanceOption'), value: ANY_OPTION },
  ...['5', '10', '15', '25'].map((option) => ({
    label: t('commerce.moreFiltersModal.withinMiles', { distance: option }, Number(option)),
    value: option
  }))
])

const closeModal = () => {
  emit('close')
}

const { apply, clear, filters } = useMoreFiltersDraft(() => props.isOpen, closeModal)
const distanceSelection = anyAwareSelection('distance')

const setIsNew = (value: unknown) => {
  filters.isNew = Boolean(value)
}

const setSmallBusiness = (value: unknown) => {
  filters.smallBusiness = Boolean(value)
}
</script>
